import { useState, useRef, useEffect } from 'react';
import { FiDownload, FiRefreshCw, FiMoreVertical } from 'react-icons/fi';

/** Sort options for session: match backend GET /assets/site/:id sortBy (location = building then room; equipment_code = building then equipment_code). */
const SESSION_SORT_OPTIONS = [
  { value: 'location', label: 'Location' },
  { value: 'equipment_code', label: 'Equipment Code' },
];

/**
 * The session breadcrumb "⋯" options menu: sort controls plus Add Asset / Export
 * PDF / Defect Report / Cancel Session actions. Owns its own open state and
 * click-outside dismissal. Renders nothing until a session is underway.
 * Extracted verbatim from TestSessionView.
 */
export function SessionOptionsMenu({
  visible,
  sessionSortBy,
  setSessionSortBy,
  sessionSortOrder,
  setSessionSortOrder,
  onAddAsset,
  onExportPdf,
  onExportDefectReport,
  onCancelSession,
  canCancelSession = false,
  sessionId,
  isSyncing,
}) {
  const [optionsMenuOpen, setOptionsMenuOpen] = useState(false);
  const optionsMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(e.target)) setOptionsMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!visible) return null;

  return (
    <div className="session-breadcrumb-options" ref={optionsMenuRef}>
      <button
        type="button"
        className="session-toolbar-options-btn"
        onClick={() => setOptionsMenuOpen((o) => !o)}
        aria-expanded={optionsMenuOpen}
        aria-haspopup="menu"
        aria-label="Options"
        title="Sort, export, add fitting"
      >
        <FiMoreVertical aria-hidden />
      </button>
      {optionsMenuOpen && (
        <div className="session-toolbar-options-popup" role="menu">
          <div className="session-toolbar-options-group">
            <span className="session-toolbar-options-group-title">Sort</span>
            <label className="session-toolbar-options-label">
              Sort by
              <select
                value={sessionSortBy}
                onChange={(e) => setSessionSortBy(e.target.value)}
                className="session-toolbar-options-select"
              >
                {SESSION_SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="session-toolbar-options-label">
              Order
              <select
                value={sessionSortOrder}
                onChange={(e) => setSessionSortOrder(e.target.value)}
                className="session-toolbar-options-select"
              >
                <option value="asc">A–Z</option>
                <option value="desc">Z–A</option>
              </select>
            </label>
          </div>
          <div className="session-toolbar-options-group">
            <button
              type="button"
              className="session-toolbar-options-item"
              onClick={() => {
                setOptionsMenuOpen(false);
                onAddAsset();
              }}
              role="menuitem"
            >
              Add Asset
            </button>
            <button
              type="button"
              className="session-toolbar-options-item"
              onClick={() => {
                onExportPdf();
                setOptionsMenuOpen(false);
              }}
              disabled={!sessionId || isSyncing}
              role="menuitem"
            >
              {isSyncing ? <FiRefreshCw className="animate-spin" /> : <FiDownload />}
              <span>Export PDF</span>
            </button>
            <button
              type="button"
              className="session-toolbar-options-item session-toolbar-options-item-danger"
              onClick={() => {
                onExportDefectReport();
                setOptionsMenuOpen(false);
              }}
              disabled={!sessionId || isSyncing}
              role="menuitem"
            >
              {isSyncing ? <FiRefreshCw className="animate-spin" /> : <FiDownload />}
              <span>Defect Report</span>
            </button>
          </div>
          {canCancelSession && (
            <div className="session-toolbar-options-group">
              <button
                type="button"
                className="session-toolbar-options-item session-toolbar-options-item-danger"
                onClick={() => {
                  setOptionsMenuOpen(false);
                  onCancelSession();
                }}
                role="menuitem"
              >
                <span>Cancel Session</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
